import React, { useState, useRef, useEffect } from 'react';
import { FiSend, FiX, FiMessageSquare, FiExternalLink } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

const NgadhuAI = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'bot', text: 'Halo! Saya **NgadhuAI**, asisten pusat data Kabupaten Ngada. Apa yang bisa saya bantu hari ini?' }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const chatEndRef = useRef(null);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) scrollToBottom();
    }, [messages, isOpen]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        const userMsg = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
        setLoading(true);

        try {
            const res = await api.post('/ai/ask', { message: userMsg });
            setMessages(prev => [...prev, { 
                role: 'bot', 
                text: res.data.data.answer,
                links: res.data.data.links
            }]);
        } catch (e) {
            setMessages(prev => [...prev, { role: 'bot', text: 'Maaf, terjadi kesalahan teknis. Silakan coba lagi nanti.' }]);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) {
        return (
            <motion.button 
                onClick={() => setIsOpen(true)}
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                className="ai-chat-bubble"
                style={{
                    width: 62, height: 62,
                    borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary-600), var(--accent-600))',
                    color: '#fff', border: 'none', cursor: 'pointer', boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
            >
                <FiMessageSquare size={28} />
                <motion.div 
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    style={{ position: 'absolute', top: -5, right: -5, background: '#ef4444', height: 22, width: 22, borderRadius: '50%', color: '#fff', fontSize: '0.6rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, border: '2px solid #fff' }}
                >
                    AI
                </motion.div>
            </motion.button>
        );
    }

    return (
        <AnimatePresence>
            <motion.div 
                initial={{ opacity: 0, y: 50, scale: 0.9, transformOrigin: 'bottom right' }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.9 }}
                className="ai-chat-window"
                style={{
                    width: 'min(360px, 85vw)', height: 'min(580px, 80vh)',
                    background: 'var(--card-bg)', borderRadius: '24px', border: '1px solid var(--glass-border)',
                    boxShadow: 'var(--shadow-xl)', display: 'flex', flexDirection: 'column', 
                    overflow: 'hidden', backdropFilter: 'var(--glass-blur)'
                }}
            >
            {/* Header */}
            <div style={{ background: 'var(--primary-700)', padding: '1.25rem', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🤖</div>
                    <div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 700 }}>NgadhuAI</div>
                        <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>Satu Data Assistant - Online</div>
                    </div>
                </div>
                <button onClick={() => setIsOpen(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><FiX size={18} /></button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {messages.map((m, i) => (
                    <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                        <div 
                            style={{ 
                                padding: '0.75rem 1rem', borderRadius: m.role === 'user' ? '18px 18px 0 18px' : '18px 18px 18px 0',
                                background: m.role === 'user' ? 'var(--primary-600)' : 'var(--gray-100)',
                                color: m.role === 'user' ? '#fff' : 'var(--text-main)',
                                fontSize: '0.85rem', lineHeight: '1.5'
                            }}
                        >
                            {m.text}
                        </div>
                        {m.links && m.links.length > 0 && (
                            <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                {m.links.map(lnk => (
                                    <Link to={`/dataset/${lnk.id}`} key={lnk.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: 'var(--card-bg)', border: '1px solid var(--gray-200)', borderRadius: '8px', fontSize: '0.75rem', color: 'var(--primary-600)', textDecoration: 'none', fontWeight: 500 }}>
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>{lnk.judul}</span>
                                        <FiExternalLink size={12} />
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
                {loading && (
                    <div style={{ alignSelf: 'flex-start', background: 'var(--gray-100)', padding: '0.75rem 1rem', borderRadius: '18px 18px 18px 0', fontSize: '0.75rem', color: 'var(--gray-500)', display: 'flex', gap: '0.4rem' }}>
                        <span className="dot-blink" style={{ animation: 'blink 1.4s infinite' }}>●</span>
                        <span className="dot-blink" style={{ animation: 'blink 1.4s infinite 0.2s' }}>●</span>
                        <span className="dot-blink" style={{ animation: 'blink 1.4s infinite 0.4s' }}>●</span>
                    </div>
                )}
                <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSend} style={{ padding: '1rem', borderTop: '1px solid var(--gray-100)' }}>
                <div style={{ position: 'relative' }}>
                    <input 
                        className="form-input" 
                        value={input} 
                        onChange={e => setInput(e.target.value)}
                        placeholder="Tanya data (misal: stunting riung)..."
                        style={{ paddingRight: '3rem', fontSize: '0.8rem', borderRadius: '25px', paddingLeft: '1.25rem' }}
                    />
                    <button 
                        type="submit" 
                        disabled={loading}
                        style={{ position: 'absolute', right: 5, top: 5, background: 'var(--primary-600)', border: 'none', width: 30, height: 30, borderRadius: '50%', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                        <FiSend size={14} />
                    </button>
                </div>
            </form>

            <style>{`
                @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                @keyframes blink { 0% { opacity: 0.2; } 20% { opacity: 1; } 100% { opacity: 0.2; } }
            `}</style>
        </motion.div>
        </AnimatePresence>
    );
};

export default NgadhuAI;
